import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import Sidebar from '../components/admin/Sidebar';
import TopNavbar from '../components/admin/TopNavbar';
import OverallRatingCard from '../components/admin/reviews/OverallRatingCard';
import ReviewFilterTabs from '../components/admin/reviews/ReviewFilterTabs';
import ReviewSearchBar from '../components/admin/reviews/ReviewSearchBar';
import ReviewCard from '../components/admin/reviews/ReviewCard';
import ReviewAnalyticsCard from '../components/admin/reviews/ReviewAnalyticsCard';
import ReplyModal from '../components/admin/reviews/ReplyModal';
import ReportModal from '../components/admin/reviews/ReportModal';
import DeleteConfirmationModal from '../components/admin/reviews/DeleteConfirmationModal';
import './ReviewsManagement.css';

const ReviewsManagement = () => {
  const navigate = useNavigate();
  const [adminSession, setAdminSession] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('mostRecent');
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [overallRating, setOverallRating] = useState({
    average: 4.6,
    totalReviews: 1234,
    growth: 0.2,
    distribution: {
      5: { count: 892, percentage: 72 },
      4: { count: 234, percentage: 19 },
      3: { count: 78, percentage: 6 },
      2: { count: 20, percentage: 2 },
      1: { count: 10, percentage: 1 }
    }
  });
  const [selectedReview, setSelectedReview] = useState(null);
  const [isReplyModalOpen, setIsReplyModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const reviewsPerPage = 20;

  useEffect(() => {
    const session = localStorage.getItem('adminSession') || sessionStorage.getItem('adminSession');
    if (!session) {
      navigate('/admin');
      return;
    }
    setAdminSession(JSON.parse(session));
  }, [navigate]);

  useEffect(() => {
    fetchReviews();
  }, [activeFilter, searchQuery, sortBy, currentPage]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      if (!supabaseUrl || supabaseUrl === 'YOUR_SUPABASE_URL') {
        // Demo mode - use sample data
        setReviews(getSampleReviews());
        setLoading(false);
        return;
      }

      // Fetch real reviews from Supabase
      // This would query the reviews table with joins to customers and orders
      
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSampleReviews = () => {
    return [
      {
        id: 1,
        customer: { name: 'Ali Ahmed', photo: null, verified: true },
        orderId: '12345',
        items: ['Chicken Biryani', 'Pizza'],
        rating: 5,
        reviewText: 'Excellent taste! Best biryani in town. Delivery was on time and food was hot. Highly recommended!',
        photos: [],
        tags: ['Fresh', 'Tasty', 'On Time', 'Hot'],
        helpfulVotes: 23,
        unhelpfulVotes: 2,
        date: '2024-12-11',
        time: '3:45 PM',
        adminReply: null,
        isReported: false
      },
      {
        id: 2,
        customer: { name: 'Sara Khan', photo: null, verified: true },
        orderId: '12346',
        items: ['Burger', 'Fries'],
        rating: 4,
        reviewText: 'Good food, but delivery was a bit late. Food quality was excellent though.',
        photos: [],
        tags: ['Tasty', 'Well Packed'],
        helpfulVotes: 15,
        unhelpfulVotes: 1,
        date: '2024-12-10',
        time: '2:30 PM',
        adminReply: {
          text: 'Thank you for your feedback! We apologize for the delay and will work on improving our delivery times.',
          date: '2024-12-10',
          time: '4:00 PM'
        },
        isReported: false
      }
    ];
  };

  const handleReply = (review) => {
    setSelectedReview(review);
    setIsReplyModalOpen(true);
  };

  const handleReport = (review) => {
    setSelectedReview(review);
    setIsReportModalOpen(true);
  };

  const handleDelete = (review) => {
    setSelectedReview(review);
    setIsDeleteModalOpen(true);
  };

  const handleSaveReply = async (replyText) => {
    // Save reply to database
    console.log('Saving reply:', replyText);
    setIsReplyModalOpen(false);
    fetchReviews();
  };

  const handleSubmitReport = async (reason, comments) => {
    // Submit report
    console.log('Submitting report:', reason, comments);
    setIsReportModalOpen(false);
    fetchReviews();
  };

  const handleConfirmDelete = async (sendNotification) => {
    // Delete review
    console.log('Deleting review:', sendNotification);
    setIsDeleteModalOpen(false);
    fetchReviews();
  };

  const filteredReviews = reviews.filter(review => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'withPhotos') return review.photos && review.photos.length > 0;
    if (activeFilter === 'reported') return review.isReported;
    if (activeFilter.includes('star')) {
      const starCount = parseInt(activeFilter.replace('star', ''));
      return review.rating === starCount;
    }
    return true;
  });

  const searchedReviews = filteredReviews.filter(review => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      review.customer.name.toLowerCase().includes(query) ||
      review.reviewText.toLowerCase().includes(query) ||
      review.orderId.includes(query)
    );
  });

  const sortedReviews = [...searchedReviews].sort((a, b) => {
    switch (sortBy) {
      case 'highestRating':
        return b.rating - a.rating;
      case 'lowestRating':
        return a.rating - b.rating;
      case 'mostHelpful':
        return (b.helpfulVotes - b.unhelpfulVotes) - (a.helpfulVotes - a.unhelpfulVotes);
      default:
        return new Date(b.date + ' ' + b.time) - new Date(a.date + ' ' + a.time);
    }
  });

  const totalPages = Math.ceil(sortedReviews.length / reviewsPerPage);
  const paginatedReviews = sortedReviews.slice(
    (currentPage - 1) * reviewsPerPage,
    currentPage * reviewsPerPage
  );

  if (!adminSession) {
    return <div className="loading-screen">Loading...</div>;
  }

  return (
    <div className="reviews-management">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="reviews-main">
        <TopNavbar 
          onMenuClick={() => setSidebarOpen(true)}
          adminName={adminSession.username || adminSession.email}
        />
        
        <main className="reviews-content">
          <div className="page-header">
            <h1 className="page-title">Reviews & Ratings</h1>
            <nav className="breadcrumb">
              <span>Dashboard</span>
              <span className="breadcrumb-separator">/</span>
              <span>Reviews</span>
            </nav>
          </div>

          <OverallRatingCard rating={overallRating} />

          <ReviewAnalyticsCard />

          <div className="filters-section">
            <ReviewFilterTabs
              activeFilter={activeFilter}
              onFilterChange={setActiveFilter}
              rating={overallRating}
            />
            <ReviewSearchBar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              sortBy={sortBy}
              onSortChange={setSortBy}
            />
          </div>

          {activeFilter !== 'all' && (
            <div className="filter-indicator">
              <span>
                Showing: {activeFilter === 'withPhotos' ? 'With Photos' : 
                         activeFilter === 'reported' ? 'Reported Reviews' :
                         `${activeFilter.replace('star', '')} Star Reviews`} ({sortedReviews.length})
              </span>
              <button onClick={() => setActiveFilter('all')}>×</button>
            </div>
          )}

          {loading ? (
            <div className="loading-state">Loading reviews...</div>
          ) : paginatedReviews.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">⭐</div>
              <h3>No reviews yet</h3>
              <p>Reviews from customers will appear here</p>
            </div>
          ) : (
            <>
              <div className="reviews-list">
                {paginatedReviews.map(review => (
                  <ReviewCard
                    key={review.id}
                    review={review}
                    onReply={() => handleReply(review)}
                    onReport={() => handleReport(review)}
                    onDelete={() => handleDelete(review)}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="pagination">
                  <div className="pagination-info">
                    Showing {(currentPage - 1) * reviewsPerPage + 1}-{Math.min(currentPage * reviewsPerPage, sortedReviews.length)} of {sortedReviews.length} reviews
                  </div>
                  <div className="pagination-buttons">
                    <button
                      className="pagination-btn"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(prev => prev - 1)}
                    >
                      Previous
                    </button>
                    {[...Array(totalPages)].map((_, i) => (
                      <button
                        key={i + 1}
                        className={`pagination-btn ${currentPage === i + 1 ? 'active' : ''}`}
                        onClick={() => setCurrentPage(i + 1)}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      className="pagination-btn"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(prev => prev + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {isReplyModalOpen && selectedReview && (
            <ReplyModal
              review={selectedReview}
              onClose={() => setIsReplyModalOpen(false)}
              onSave={handleSaveReply}
            />
          )}

          {isReportModalOpen && selectedReview && (
            <ReportModal
              review={selectedReview}
              onClose={() => setIsReportModalOpen(false)}
              onSubmit={handleSubmitReport}
            />
          )}

          {isDeleteModalOpen && selectedReview && (
            <DeleteConfirmationModal
              review={selectedReview}
              onClose={() => setIsDeleteModalOpen(false)}
              onConfirm={handleConfirmDelete}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default ReviewsManagement;

